import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import OverflowMenuVertical20 from '@carbon/icons-react/es/overflow-menu--vertical/20';
import { Dropdown, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Comment } from 'api/document/types/document';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { showConfirmPopup } from 'components/atoms/show-confirm-popup';
import { UserInfoAction } from 'components/atoms/user-info-actions';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import styles from './comment-head.module.css';

interface Props {
  isThreadComment: boolean;
  shouldHide: boolean;
  authorClass: string;
  actionClass: string;
  comment: Comment;
  onPerformEdit: (comment: Comment) => void;
  onPerformDelete: (comment: Comment) => void;
  onPerformThreadDelete: (comment: Comment) => void;
  onPerformThreadResolve: (comment: Comment) => void;
  projectId: number;
}

const KebabMenu = ({
  isThreadComment,
  onPerformEdit,
  onPerformDelete,
  onPerformThreadDelete,
  onVisibilityChange,
  canDelete,
  canEdit,
}) => {
  const [visible, handleVisibleChange] = useState(false);
  const confirmPopupTitleText = useTranslation('delete_confirmation_message');
  const confirmPopupOkText = useTranslation('yes');
  const confirmPopupCancelText = useTranslation('no');
  const EDIT = 'edit';
  const DELETE = 'delete';
  const deleteFnToCall = isThreadComment
    ? onPerformThreadDelete
    : onPerformDelete;
  const handleMenuItemClick = (e) => {
    switch (e.key) {
      case EDIT:
        onPerformEdit(true);

        return handleVisibleChange(false);
      case DELETE:
        return showConfirmPopup(
          {
            title: confirmPopupTitleText,
            ok: confirmPopupOkText,
            cancel: confirmPopupCancelText,
          },
          deleteFnToCall,
        );
      default:
        return null;
    }
  };

  const deleteLabel = isThreadComment ? 'delete_thread' : 'delete_comment';

  const DropMenu = (
    <Menu onClick={handleMenuItemClick}>
      {canEdit ? (
        <Menu.Item
          key={EDIT}
          icon={<Edit16 className="anticon anticon-user" />}
        >
          <FormattedMessage id="edit_comment" />
        </Menu.Item>
      ) : null}
      {canDelete ? (
        <Menu.ItemDelete key={DELETE}>
          <FormattedMessage id={deleteLabel} />
        </Menu.ItemDelete>
      ) : null}
    </Menu>
  );

  return (
    <Dropdown
      placement="bottomRight"
      overlay={DropMenu}
      trigger={['click']}
      onVisibleChange={(flag) => {
        onVisibilityChange(flag);
        handleVisibleChange(flag);
      }}
      visible={visible}
    >
      <Button type="text" icon={<OverflowMenuVertical20 />} />
    </Dropdown>
  );
};

export const CommentHead: FC<Props> = ({
  comment,
  isThreadComment,
  shouldHide,
  authorClass = '',
  actionClass = '',
  onPerformEdit,
  onPerformDelete,
  onPerformThreadDelete,
  onPerformThreadResolve,
  projectId,
}: Props) => {
  const [isDropdownVisible, setDropdownVisibilityChange] = useState(false);
  const userInfo = useUser();
  const moreActions = useTranslation('more_actions');
  const resolve = useTranslation('resolve_discussion');
  const kebabClasses = classNames(styles.actions, actionClass, {
    [styles.actions__visible]: isDropdownVisible,
  });
  const projectResource = useProjectAsRBACResource(projectId);
  const { hasAccessToResource } = useWithAccess();
  const hasEditCommentAccess = hasAccessToResource({
    // rule: 'DOCUMENT_COMMENT:WRITE',
    rule: 'DOCUMENT:WRITE',
    resourceData: projectResource,
  });
  const hasDeleteCommentAccess = hasAccessToResource({
    // rule: 'DOCUMENT_COMMENT:DELETE',
    rule: 'DOCUMENT:WRITE',
    resourceData: projectResource,
  });
  const EditCommentActions = () => (
    <div className={styles.actions_wrapper}>
      {isThreadComment && hasEditCommentAccess ? (
        <div className={actionClass}>
          <Tooltip placement="topLeft" title={resolve}>
            <Button
              type="text"
              icon={<CheckmarkOutline16 />}
              onClick={() => onPerformThreadResolve(comment)}
            />
          </Tooltip>
        </div>
      ) : null}
      {userInfo?.user.userId === comment.user.userId &&
      (hasEditCommentAccess || hasDeleteCommentAccess) ? (
        <Tooltip placement="topLeft" title={moreActions}>
          <div className={kebabClasses}>
            <KebabMenu
              isThreadComment={isThreadComment}
              onVisibilityChange={(visibility) =>
                setDropdownVisibilityChange(visibility)
              }
              onPerformEdit={() => onPerformEdit(comment)}
              onPerformDelete={() => onPerformDelete(comment)}
              onPerformThreadDelete={() => onPerformThreadDelete(comment)}
              canEdit={hasEditCommentAccess}
              canDelete={hasDeleteCommentAccess}
            />
          </div>
        </Tooltip>
      ) : null}
    </div>
  );

  return !shouldHide ? (
    <UserInfoAction
      user={comment.user}
      createdAt={comment.createdAt}
      userClass={authorClass}
      className={styles.wrapper}
      action={EditCommentActions}
    />
  ) : null;
};
